import { useLocation, useNavigate } from 'react-router';
import { useEffect, useMemo, useState } from 'react';

import { pages } from 'pages';
import { OnboardingMilestone } from 'api/types';
import { addressesMatch } from 'shared/utils/adderessesMatch';
import {
  AnswerNetVideoTPVStatusType,
  ONBOARDING_STEP_PARAM,
  OnboardingStep,
} from '../modules/types/util';
import { useUser } from './useUser';

export const useOnboardingStep = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const navigate = useNavigate();
  const { userResult, selectedAgreement } = useUser();
  const [completedSubtasks, setCompletedSubtasks] = useState<Set<OnboardingStep>>(new Set());

  const user = userResult?.user;
  let showConfirmAddressStep = true;

  if (
    user?.address &&
    selectedAgreement?.system.address &&
    !user.address.confirmed &&
    addressesMatch(user?.address, selectedAgreement?.system.address)
  ) {
    // if a users billing and site address are the same we don't need to show them the step to confirm their address
    showConfirmAddressStep = false;
  }

  const onboardingStepParam = query.get(ONBOARDING_STEP_PARAM);
  const enrolledInAch =
    Boolean(selectedAgreement?.ach_elected) && Boolean(selectedAgreement?.ach_verified);
  const userId = user?.id;
  const status = selectedAgreement?.users_tpv_statuses?.find(
    (el: { user_id: string; tpv_status: string }) => el.user_id === userId,
  );
  const tpvCompleted = status?.tpv_status === AnswerNetVideoTPVStatusType.VERIFIED;
  const confirmedAddress = Boolean(user?.address?.confirmed);

  useEffect(() => {
    const next = new Set<OnboardingStep>();

    if (selectedAgreement?.reviewed_by_user) {
      next.add(OnboardingStep.REVIEW_AGREEMENT);
    }

    if (enrolledInAch) {
      next.add(OnboardingStep.AUTOPAY);
    }

    if (tpvCompleted) {
      next.add(OnboardingStep.TPV);
    }

    if (showConfirmAddressStep && confirmedAddress) {
      next.add(OnboardingStep.CONFIRM_ADDRESS);
    }

    setCompletedSubtasks(next);
  }, [
    confirmedAddress,
    enrolledInAch,
    selectedAgreement?.reviewed_by_user,
    showConfirmAddressStep,
    tpvCompleted,
  ]);

  const currentStep: OnboardingStep = useMemo(() => {
    if (onboardingStepParam) return Number(onboardingStepParam) as OnboardingStep;
    const currentMilestone = selectedAgreement?.onboarding_milestone;

    switch (currentMilestone) {
      case OnboardingMilestone.PRE_INSTALLATION:
        if (!selectedAgreement?.reviewed_by_user) {
          return OnboardingStep.REVIEW_AGREEMENT;
        }
        if (!tpvCompleted) {
          return OnboardingStep.TPV;
        }
        if (!enrolledInAch) {
          return OnboardingStep.AUTOPAY;
        }
        if (showConfirmAddressStep && !confirmedAddress) {
          return OnboardingStep.CONFIRM_ADDRESS;
        }
        return OnboardingStep.PRE_INSTALLATION;

      case OnboardingMilestone.SYSTEM_INSTALLATION:
        return OnboardingStep.SYSTEM_INSTALLATION;

      case OnboardingMilestone.SYSTEM_ACTIVATION:
        return OnboardingStep.SYSTEM_ACTIVATION;

      default:
        return OnboardingStep.INTRODUCTION;
    }
  }, [
    confirmedAddress,
    enrolledInAch,
    onboardingStepParam,
    selectedAgreement?.onboarding_milestone,
    selectedAgreement?.reviewed_by_user,
    showConfirmAddressStep,
    tpvCompleted,
  ]);

  const goToStep = (step: OnboardingStep | null) => {
    const pathTo = `/${pages.STATUS}?${ONBOARDING_STEP_PARAM}=${step}`;

    navigate(pathTo);
  };

  return {
    currentStep,
    goToStep,
    completedSubtasks,
    showConfirmAddressStep,
  };
};

export default useOnboardingStep;
